/**
 * Printable QR poster for the public register link.
 * ----------------------------------------------------------------
 *  • QR image comes from GET /api/qr?url=... (uses Python qrcode lib server-side)
 *  • Poster laid out for A4 portrait — click "Print" for the browser dialog
 *  • Also supports downloading the raw QR PNG for use in ads / WhatsApp status
 *  • Admin can customise: destination URL, headline, subtitle, sub-text
 */
import { useMemo, useRef, useState } from "react";
import { Printer, Download, Sparkles, Copy, Check, Store } from "lucide-react";
import { toast } from "sonner";

// Default to whatever origin the browser is currently on — so once the custom
// domain is live (e.g., hardinbachat.com), the QR automatically points there.
// Admin can still edit the URL field manually before printing.
const DEFAULT_URL =
    (typeof window !== "undefined" && window.location.origin)
    ? `${window.location.origin}/signup`
    : "";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HDB_LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_pdf-guide-dev/artifacts/7s3f15d0_HDB%20LOGO.webp";

export default function QRPosterPage() {
    const [url, setUrl] = useState(DEFAULT_URL);
    const [headline, setHeadline] = useState("Sign up in a minute.");
    const [tagline, setTagline] = useState("“What we deliver is savings.\nRest, all is just delivery.”");
    const [subtext, setSubtext] = useState("Scan · Sign up · Start ordering");
    const [copied, setCopied] = useState(false);
    const posterRef = useRef(null);

    const qrSrc = useMemo(() => {
        const enc = encodeURIComponent(url);
        return `${BACKEND}/api/qr?url=${enc}&size=16`;
    }, [url]);

    const print = () => {
        window.print();
    };

    const downloadQR = () => {
        const a = document.createElement("a");
        a.href = qrSrc;
        a.download = "hdb-portal-qr.png";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch { toast.error("Couldn't copy — please select the URL manually"); }
    };

    return (
        <div data-testid="qr-poster-page">
            {/* Controls — hidden when printing */}
            <div className="print:hidden mb-6">
                <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
                    <div>
                        <h1 className="text-3xl hdb-heading font-black text-slate-900">QR Poster</h1>
                        <p className="text-slate-500 mt-1 text-sm">
                            Print this for your storefront · share the QR image in WhatsApp status / flyers /
                            newspaper ads.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={downloadQR} className="hdb-btn hdb-btn-ghost" data-testid="qr-download-btn">
                            <Download size={13}/> QR PNG
                        </button>
                        <button onClick={print} className="hdb-btn hdb-btn-primary" data-testid="qr-print-btn">
                            <Printer size={13}/> Print poster
                        </button>
                    </div>
                </div>

                <div className="hdb-card p-4 grid md:grid-cols-2 gap-4">
                    <Field label="Destination URL"
                           value={url} onChange={setUrl}
                           testid="qr-url"
                           helper="Where scanners land — usually the public register page">
                        <button onClick={copyUrl} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
                                data-testid="qr-copy-url">
                            {copied ? <Check size={11}/> : <Copy size={11}/>} Copy link
                        </button>
                    </Field>
                    <Field label="Headline"
                           value={headline} onChange={setHeadline} testid="qr-headline"/>
                    <div className="md:col-span-2">
                        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block mb-1">
                            Tagline
                        </label>
                        <textarea value={tagline} onChange={(e) => setTagline(e.target.value)}
                                  rows={2}
                                  className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:border-slate-900 text-sm"
                                  data-testid="qr-tagline"/>
                    </div>
                    <Field label="Sub-text below QR"
                           value={subtext} onChange={setSubtext} testid="qr-subtext"/>
                </div>
            </div>

            {/* Poster — visible on screen (scaled) and expands to A4 for print */}
            <div ref={posterRef} className="poster-a4 mx-auto"
                 data-testid="qr-poster-preview">
                <div className="h-full flex flex-col p-10 relative overflow-hidden bg-white">
                    {/* Green accent bars */}
                    <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500"/>
                    <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-teal-500 via-emerald-500 to-emerald-600"/>
                    <div className="absolute -right-20 top-20 w-64 h-64 rounded-full bg-emerald-100/70"/>
                    <div className="absolute -left-20 bottom-32 w-56 h-56 rounded-full bg-yellow-100/70"/>

                    <div className="relative flex-1 flex flex-col">
                        {/* Header */}
                        <div className="flex items-center gap-4">
                            <div className="w-20 h-20 rounded-2xl bg-white shadow-md p-2 grid place-items-center border-2 border-emerald-200">
                                <img src={HDB_LOGO} alt="HDB" className="w-full h-full object-contain"/>
                            </div>
                            <div>
                                <div className="text-4xl font-black text-slate-900 leading-none"
                                     style={{fontFamily:"'Chivo',sans-serif"}}>Har Din Bachat</div>
                                <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mt-1">HDB Retail · Home Delivery</div>
                            </div>
                        </div>

                        {/* Headline */}
                        <h2 className="text-5xl font-black text-slate-900 mt-8 leading-tight"
                            style={{fontFamily:"'Chivo',sans-serif"}}>
                            {headline}
                        </h2>
                        <div className="text-xl italic text-slate-700 mt-3 whitespace-pre-line font-medium">
                            {tagline}
                        </div>

                        {/* Promise line — headline strength */}
                        <div className="mt-4 py-3 px-4 bg-emerald-50 border-l-4 border-emerald-600 rounded-r-lg">
                            <div className="text-base font-black text-emerald-900 leading-snug"
                                 data-testid="qr-poster-promise">
                                Any and every monthly essential
                                <span className="block text-emerald-700 font-bold text-sm">delivered right to your doorstep.</span>
                            </div>
                        </div>

                        {/* QR block */}
                        <div className="flex-1 grid place-items-center mt-2">
                            <div className="text-center">
                                <div className="inline-block p-5 bg-white rounded-2xl shadow-xl border-4 border-emerald-600 relative">
                                    <img src={qrSrc} alt="Scan to register"
                                         className="w-[280px] h-[280px] block"
                                         data-testid="qr-poster-image"/>
                                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full">
                                        Scan me
                                    </div>
                                </div>
                                <div className="mt-8 text-2xl font-black text-slate-900 uppercase tracking-wide">
                                    {subtext}
                                </div>
                                <div className="mt-2 text-sm text-slate-500 font-mono break-all">
                                    {url}
                                </div>
                            </div>
                        </div>

                        {/* Perks strip */}
                        <div className="grid grid-cols-3 gap-3 mt-6 text-center">
                            <Perk emoji="🏠" title="Home delivery"/>
                            <Perk emoji="🎙️" title="Voice order"/>
                            <Perk emoji="💸" title="No account fee"/>
                        </div>

                        {/* Footer */}
                        <div className="mt-4 pt-4 border-t border-slate-200 text-center text-xs text-slate-500">
                            <b className="text-slate-700">Households + Businesses welcome</b> ·
                            Instant portal access · GSTIN optional
                        </div>
                    </div>
                </div>
            </div>

            {/* Print-only CSS */}
            <style>{`
                .poster-a4 {
                    width: 210mm; height: 297mm;
                    box-shadow: 0 8px 40px rgba(15,23,42,0.15);
                    border-radius: 8px;
                    overflow: hidden;
                    transform-origin: top center;
                }
                @media screen and (max-width: 900px) {
                    .poster-a4 { transform: scale(0.42); margin-bottom: -170mm; }
                }
                @media screen and (min-width: 901px) and (max-width: 1300px) {
                    .poster-a4 { transform: scale(0.7); margin-bottom: -90mm; }
                }
                @media print {
                    body, html { background: white !important; margin: 0; }
                    .app-sidebar, .hdb-topbar, .app-main-header { display: none !important; }
                    .poster-a4 {
                        width: 210mm; height: 297mm;
                        box-shadow: none; border-radius: 0;
                        transform: none; margin: 0;
                    }
                    @page { size: A4 portrait; margin: 0; }
                }
            `}</style>
        </div>
    );
}


function Field({ label, value, onChange, testid, children, helper }) {
    return (
        <div>
            <div className="flex items-center justify-between">
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block mb-1">
                    {label}
                </label>
                {children}
            </div>
            <input value={value} onChange={(e) => onChange(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:border-slate-900 text-sm"
                   data-testid={testid}/>
            {helper && <div className="text-[10px] text-slate-400 mt-1">{helper}</div>}
        </div>
    );
}


function Perk({ emoji, title }) {
    return (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 py-2 px-1">
            <div className="text-2xl">{emoji}</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 mt-1">{title}</div>
        </div>
    );
}
